import { PvaData } from "@inrange/api-clients";
import * as L from "leaflet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import BoundaryEditorMap, { EditableBuilding } from "./BoundaryEditorMap";
import { BulkReviewItemHelpers } from "./BulkReviewPanel";
import PvLayoutReviewMap from "./PvLayoutReviewMap";
import {
  getBoundaryReviewedAt,
  getPvLayoutQuality,
  PvLayoutQuality,
} from "./reviewState";

export type { PvLayoutQuality } from "./reviewState";

interface BulkReviewBoundaryAndRoofItemProps {
  helpers: BulkReviewItemHelpers;
  isRecomputingPv: boolean;
  isSaving: boolean;
  /**
   * PVA query state is owned by the wrapper (via useQueries across
   * all visited sites), so the chip can flip Loading -> Pending even
   * after the operator moves off this row. We just consume the
   * resolved data + flags as props.
   */
  pvaData: PvaData | undefined;
  pvaIsFetching: boolean;
  pvaIsError: boolean;
  onAcceptBoundary: (currentBuildings: Record<string, EditableBuilding>) => void;
  onMarkPvQuality: (quality: PvLayoutQuality) => void;
}

/**
 * Two-stage per-site review surface for the building boundary + PV
 * layout flow. Stage 1 mounts the boundary editor in always-edit
 * mode; Accept fires whether the operator dragged or not. Stage 2
 * shows the real PV layout from PVA and the Cmd+. toggle for hiding
 * the panels layer.
 *
 * Stage is normally derived from boundaryReviewedAt, but the operator
 * can override back to "boundary" from stage 2 via the [Back to
 * boundary] button. The override resets on row-switch because we
 * mount with key=site.id at the parent.
 */
const BulkReviewBoundaryAndRoofItem: React.FC<
  BulkReviewBoundaryAndRoofItemProps
> = ({
  helpers,
  isRecomputingPv,
  isSaving,
  pvaData,
  pvaIsFetching,
  pvaIsError,
  onAcceptBoundary,
  onMarkPvQuality,
}) => {
  const { site, index, total, skip } = helpers;
  const [forcedStage, setForcedStage] = useState<"boundary" | null>(null);
  const stage: "boundary" | "pv" =
    forcedStage ?? (getBoundaryReviewedAt(site) ? "pv" : "boundary");

  const initialBuildings = useRef<EditableBuilding[]>(
    site.buildings as EditableBuilding[]
  );
  const [currentBuildings, setCurrentBuildings] = useState<
    Record<string, EditableBuilding>
  >(() => {
    const seeded: Record<string, EditableBuilding> = {};
    site.buildings.forEach((b, ii) => {
      const key = b.id ?? `${b.geometry?.type}-${ii}`;
      seeded[key] = b as EditableBuilding;
    });
    return seeded;
  });

  const siteCenter: [number, number] = useMemo(
    () => [site.latitude, site.longitude],
    [site.latitude, site.longitude]
  );

  // Single ref held to whichever map is currently mounted, so the
  // Y/T zoom shortcuts can target it.
  const activeMapRef = useRef<L.Map | null>(null);
  const setActiveMap = useCallback((m: L.Map | null | undefined) => {
    activeMapRef.current = m ?? null;
  }, []);

  const currentQuality = getPvLayoutQuality(site);

  // Keyboard shortcuts. Stage 1: [F] Accept, [J] Skip. Stage 2: [1]
  // High, [2] Medium, [3] Low, [B] Back to boundary, [J] Skip.
  // Ignored when focus is in an input/textarea/contenteditable, and
  // when any modifier is held - this leaves Cmd+. / Ctrl+. (PV
  // toggle on the stage-2 map) free.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      const tag = tgt?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        tgt?.isContentEditable
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();

      // ↑ / ↓ are handled at the BulkReviewPanel level - they just
      // scroll the left list pane, they don't change selection.

      // Y / T zoom shortcuts apply on both stages and ignore the
      // saving / recomputing gates (they're purely view controls).
      if (key === "y") {
        e.preventDefault();
        activeMapRef.current?.zoomIn();
        return;
      }
      if (key === "t") {
        e.preventDefault();
        activeMapRef.current?.zoomOut();
        return;
      }

      if (stage === "boundary") {
        if (isSaving) return;
        if (key === "f") {
          e.preventDefault();
          onAcceptBoundary(currentBuildings);
        } else if (key === "j") {
          e.preventDefault();
          skip();
        }
        return;
      }

      // stage === "pv"
      if (key === "b") {
        e.preventDefault();
        setForcedStage("boundary");
        return;
      }
      if (key === "j") {
        if (isSaving) return;
        e.preventDefault();
        skip();
        return;
      }
      if (isSaving || isRecomputingPv) return;
      if (key === "1") {
        e.preventDefault();
        onMarkPvQuality("high");
      } else if (key === "2") {
        e.preventDefault();
        onMarkPvQuality("medium");
      } else if (key === "3") {
        e.preventDefault();
        onMarkPvQuality("low");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    stage,
    isSaving,
    isRecomputingPv,
    currentBuildings,
    onAcceptBoundary,
    onMarkPvQuality,
    skip,
  ]);

  return (
    <div
      style={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        height: "100%",
        minHeight: 0,
      }}
      data-testid="bulk-review-item"
    >
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: "#666" }}>
            Site {index + 1} of {total} ·{" "}
            {stage === "boundary"
              ? "Stage 1 – boundary"
              : "Stage 2 – PV layout"}
          </div>
          <div style={{ margin: "4px 0", fontSize: 15, fontWeight: 600 }}>
            <a
              href={`/site/${site.id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {site.name || site.id}
            </a>
          </div>
        </div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {pvaData?.site_image?.cached_at && (
            <div
              style={{ fontSize: 11, color: "#6c757d", fontStyle: "italic" }}
              data-testid="bulk-review-cached-imagery-notice"
            >
              Satellite imagery cached{" "}
              {pvaData.site_image.cached_at.replace(/T.*/, "")}
            </div>
          )}
          <a
            href={`https://maps.google.com/?q=${site.latitude},${site.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13 }}
            data-testid="bulk-review-google-maps-link"
          >
            View in Google Maps
          </a>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 320 }}>
        {stage === "boundary" ? (
          <BoundaryEditorMap
            siteCenter={siteCenter}
            buildings={initialBuildings.current}
            onChange={setCurrentBuildings}
            onMapReady={setActiveMap}
          />
        ) : (
          <PvLayoutReviewMap
            siteId={site.id}
            siteCenter={siteCenter}
            buildings={site.buildings}
            pvaData={pvaData}
            isFetching={pvaIsFetching}
            isError={pvaIsError}
            isRecomputing={isRecomputingPv}
            onMapReady={setActiveMap}
          />
        )}
      </div>

      {stage === "boundary" ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            variant="primary"
            onClick={() => onAcceptBoundary(currentBuildings)}
            disabled={isSaving}
            data-testid="bulk-review-accept-boundary"
          >
            {isSaving ? "Saving…" : "Accept"} <KeyHint k="F" />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={skip}
            disabled={isSaving}
            data-testid="bulk-review-skip"
          >
            Skip <KeyHint k="J" />
          </Button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            variant={currentQuality === "high" ? "success" : "outline-success"}
            onClick={() => onMarkPvQuality("high")}
            disabled={isSaving || isRecomputingPv}
            data-testid="bulk-review-pv-high"
          >
            High quality <KeyHint k="1" />
          </Button>
          <Button
            variant={
              currentQuality === "medium" ? "warning" : "outline-warning"
            }
            onClick={() => onMarkPvQuality("medium")}
            disabled={isSaving || isRecomputingPv}
            data-testid="bulk-review-pv-medium"
          >
            Medium quality <KeyHint k="2" />
          </Button>
          <Button
            variant={currentQuality === "low" ? "danger" : "outline-danger"}
            onClick={() => onMarkPvQuality("low")}
            disabled={isSaving || isRecomputingPv}
            data-testid="bulk-review-pv-low"
          >
            Low quality <KeyHint k="3" />
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => setForcedStage("boundary")}
            disabled={isSaving}
            data-testid="bulk-review-back-to-boundary"
          >
            Back to boundary <KeyHint k="B" />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={skip}
            disabled={isSaving}
            data-testid="bulk-review-skip"
          >
            Skip <KeyHint k="J" />
          </Button>
        </div>
      )}
    </div>
  );
};

function KeyHint({ k }: { k: string }) {
  return (
    <kbd
      style={{
        fontSize: 10,
        padding: "1px 5px",
        marginLeft: 4,
        borderRadius: 3,
        backgroundColor: "rgba(0,0,0,0.18)",
        color: "inherit",
        fontFamily: "inherit",
        fontWeight: 600,
      }}
    >
      {k}
    </kbd>
  );
}

export default BulkReviewBoundaryAndRoofItem;
